<!-- ANCHOR: FAB -->
@if (isArsonProfile()) {
<button
  class="chatbot-fab"
  type="button"
  [class.chatbot-fab--dragging]="isDragging()"
  [attr.aria-label]="'chatbot.aria-open' | translate"
  [attr.aria-expanded]="isOpen()"
  (click)="onFabClick()"
  (pointerdown)="onFabPointerDown($event)"
>
  @if (isOpen()) {
    <i class="fas fa-times" aria-hidden="true"></i>
  } @else {
    <i class="fas fa-comments" aria-hidden="true"></i>
  }
</button>

<!-- ANCHOR: Chat Panel -->
<!-- Chat panel — shown only when open -->
@if (isOpen()) {
  <dialog
    class="chatbot-panel"
    open
    [attr.aria-label]="'chatbot.aria-panel' | translate"
    aria-modal="false"
  >
    <!-- ANCHOR: Header -->
    <div class="chatbot-header" (pointerdown)="onHeaderPointerDown($event)">
      <div class="chatbot-header__title">
        <img
          class="chatbot-header__avatar"
          src="assets/chatbot/chatbot-assistant.png"
          alt=""
          aria-hidden="true"
          [style.display]="avatarLoadFailed() ? 'none' : ''"
          (error)="avatarLoadFailed.set(true)"
        />
        <span>{{ chatbotTitle() }}</span>
      </div>
    </div>

    <!-- ANCHOR: Quick Actions Toolbar -->
    <!-- Compact toolbar with structure and instructions shortcuts. -->
    <div class="chatbot-toolbar" role="toolbar" [attr.aria-label]="'chatbot.aria-toolbar' | translate">
      <button
        type="button"
        class="chatbot-toolbar__btn"
        [attr.aria-label]="'chatbot.aria-screen-help' | translate"
        [disabled]="isTyping()"
        (click)="explainScreen()"
      >
        <i class="fas fa-question-circle" aria-hidden="true"></i>
        {{ 'chatbot.btn-screen-help' | translate }}
      </button>
      <button
        type="button"
        class="chatbot-toolbar__btn"
        (click)="openTree()"
      >
        <i class="fas fa-code-branch" aria-hidden="true"></i>
        {{ 'chatbot.btn-structure' | translate }}
      </button>
      <!-- Opens the read-only intents reference panel — ARSON only -->
      @if (isArsonProfile()) {
        <button
          type="button"
          class="chatbot-toolbar__btn"
          [attr.aria-label]="'chatbot.aria-open-intents' | translate"
          (click)="openInstructions()"
        >
          <i class="fas fa-lightbulb" aria-hidden="true"></i>
          {{ 'chatbot.btn-instructions' | translate }}
        </button>
      }
      <!-- Font size controls: right-aligned, A- decrements, A+ increments (steps 13/15/17/20px) -->
      <div class="chatbot-toolbar__font-controls">
        <button
          type="button"
          class="chatbot-toolbar__btn chatbot-toolbar__btn--icon"
          [disabled]="fontSizeIdx() === 0"
          (click)="decreaseFontSize()"
          title="Reduce text size"
        >
          <span class="chatbot-toolbar__font-label">A<sup>-</sup></span>
        </button>
        <button
          type="button"
          class="chatbot-toolbar__btn chatbot-toolbar__btn--icon"
          [disabled]="fontSizeIdx() === FONT_STEPS.length - 1"
          (click)="increaseFontSize()"
          title="Increase text size"
        >
          <span class="chatbot-toolbar__font-label">A<sup>+</sup></span>
        </button>
      </div>
    </div>

    <!-- ANCHOR: Body — Chat -->
      <!-- ANCHOR: Message List -->
      <div
        class="chatbot-messages"
        #messagesContainer
        role="log"
        aria-live="polite"
        [style.--chatbot-bubble-font-size]="fontSize() + 'px'"
        [attr.aria-label]="'chatbot.aria-history' | translate"
      >
        @for (msg of renderedMessages(); track msg.id) {
          <div
            class="chatbot-bubble"
            [attr.data-msg-id]="msg.id"
            [class.chatbot-bubble--user]="msg.role === 'user'"
            [class.chatbot-bubble--bot]="msg.role === 'bot'"
            [class.chatbot-bubble--help]="!!msg.blocks"
          >
            @if (msg.blocks; as blocks) {
              <div class="chatbot-help">
                @for (block of blocks; track $index) {
                  @if (block.kind === 'gap') {
                    <div class="chatbot-help__gap" aria-hidden="true"></div>
                  } @else {
                    <p
                      [class]="'chatbot-help__line chatbot-help__line--' + block.kind"
                      [attr.role]="block.kind === 'title' || block.kind === 'heading' ? 'heading' : null"
                      [attr.aria-level]="block.kind === 'title' ? 3 : block.kind === 'heading' ? 4 : null"
                    >
                      @for (seg of block.segments; track $index) {
                        @switch (seg.kind) {
                          @case ('label') { <strong class="chatbot-help__label">{{ seg.text }}:</strong> }
                          @case ('quote') { <mark class="chatbot-help__quote">{{ seg.text }}</mark> }
                          @default { <span>{{ seg.text }}</span> }
                        }
                      }
                    </p>
                  }
                }
              </div>
            } @else {
              <p class="chatbot-bubble__text">{{ msg.content }}</p>
            }
          </div>
        }

        @if (isTyping()) {
          <div
            class="chatbot-bubble chatbot-bubble--bot chatbot-bubble--typing"
            [attr.aria-label]="'chatbot.aria-typing' | translate"
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        }
      </div>

      <!-- ANCHOR: Input Area -->
      <div class="chatbot-input">
        <input
          #chatInput
          type="text"
          class="chatbot-input__field"
          [placeholder]="'chatbot.placeholder' | translate"
          [attr.aria-label]="'chatbot.aria-input' | translate"
          autocomplete="off"
          [value]="inputText()"
          (input)="inputText.set(getInputValue($event))"
          (keydown)="onKeydown($event)"
        />
        <button
          type="button"
          class="chatbot-input__send"
          [attr.aria-label]="'chatbot.aria-send' | translate"
          [disabled]="!canSend()"
          (click)="send()"
        >
          <i class="fas fa-paper-plane" aria-hidden="true"></i>
        </button>
        <!-- Clear chat messages -->
        <button
          type="button"
          class="chatbot-input__clear"
          [attr.aria-label]="'chatbot.aria-clear-chat' | translate"
          (click)="clearChat()"
          title="Clear chat"
        >
          <i class="fas fa-trash-alt" aria-hidden="true"></i>
        </button>
      </div>

  </dialog>
}

<!-- ANCHOR: App Tree Dialog (full-screen, rendered outside the chatbot panel) -->
@if (isTreeOpen()) {
  <app-chatbot-app-tree
    (closed)="closeTree()"
    (navigate)="navigateFromTree($event)"
  ></app-chatbot-app-tree>
}

<!-- ANCHOR: Instructions Dialog (full-screen, rendered outside the chatbot panel) -->
@if (isArsonProfile() && isInstructionsOpen()) {
  <app-chatbot-instructions
    (closed)="closeInstructionsDialog()"
    (exampleClicked)="onExampleClicked($event)"
  ></app-chatbot-instructions>
}
} <!-- end @if (isArsonProfile()) -->

